import { z } from 'zod';
import { ACCOUNT_TYPES } from '../constants/accountTypes.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

export const IMPORT_TYPES = [
  'trial_balance',
  'transactions',
  'chart_of_accounts',
  'contacts',
] as const;
export type ImportType = (typeof IMPORT_TYPES)[number];

export const IMPORT_STATUSES = [
  'pending',
  'validated',
  'committed',
  'rolled_back',
  'failed',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

// ── Account mapping (trial balance + transactions) ───────────────────────────

export const newAccountSchema = z.object({
  // Optional — blank means the server auto-assigns a code.
  code: z.string().trim().max(20).optional(),
  name: z.string().trim().min(1).max(200),
  accountType: z.enum(ACCOUNT_TYPES),
  accountSubtype: z.string().trim().max(50).optional(),
});

export const accountMappingSchema = z.object({
  action: z.enum(['map', 'create', 'skip']),
  accountId: z.string().uuid().optional(),
  newAccount: newAccountSchema.optional(),
});
export type AccountMapping = z.infer<typeof accountMappingSchema>;

export const importMappingsSchema = z.record(z.string(), accountMappingSchema);
export type ImportMappings = z.infer<typeof importMappingsSchema>;

// ── Requests ──────────────────────────────────────────────────────────────────

export const uploadImportInputSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  content: z.string().min(1),
});
export type UploadImportInput = z.infer<typeof uploadImportInputSchema>;

export const commitImportInputSchema = z.object({
  mappings: importMappingsSchema.optional(),
  asOfDate: isoDate.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  /**
   * Import transactions even when their number already exists in the books from a
   * prior import. Off by default (re-import protection). Turn on for older books
   * where journal-entry numbers were reused across genuinely different entries.
   */
  allowDuplicates: z.boolean().optional(),
});
export type CommitImportInput = z.infer<typeof commitImportInputSchema>;

// ── Responses ─────────────────────────────────────────────────────────────────

export interface ValidationMessage {
  severity: 'error' | 'warning';
  message: string;
  row?: number;
}

export interface ImportSummary {
  entriesCreated: number;
  accountsCreated: number;
  contactsCreated: number;
}

export interface ImportSession {
  id: string;
  companyId: string;
  importType: ImportType;
  status: ImportStatus;
  fileName: string;
  asOfDate: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  rowCount: number;
  rollbackAvailable: boolean;
  summary: ImportSummary | null;
  committedAt: string | null;
  rolledBackAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One QBO account name encountered in a trial-balance / transactions import. */
export interface ImportAccountRef {
  qboAccountName: string;
  /** Suggested mapping (matched existing account, or a create stub). */
  suggestion: AccountMapping;
  matchedAccountCode?: string;
}

export interface ImportPreview {
  session: ImportSession;
  importType: ImportType;
  /** Distinct QBO accounts needing a mapping (trial_balance / transactions only). */
  accounts: ImportAccountRef[];
  /** Lightweight preview rows for display (shape varies by import type). */
  preview: unknown;
  validation: ValidationMessage[];
}

export interface CommitImportResult {
  session: ImportSession;
  committed: boolean;
  validation: ValidationMessage[];
}
